import { ErrorMessage, Field, FieldArray, Form } from "formik";
import React, { Fragment, useState } from "react";
import {
    Button,
    FormCheck,
    FormControl,
    FormGroup,
    FormLabel,
    FormText,
    Spinner,
} from "react-bootstrap";
import FormListButton from "src/components/Shared/SharedFormComponents/FormListButton";
import FormListSingleFieldItem from "src/components/Shared/SharedFormComponents/FormListSingleFieldItem";
import styles from "../RetailerSettings.module.scss";
import { ADDRESS_INITIAL_VALUES } from "../RetailerSettingsConstants";
import Api from "src/data/api";
import { useShopifyAppBridge } from "src/components/shopify/appBridge";
import { Redirect } from "@shopify/app-bridge/actions";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { showNotification } from "src/redux/notificationActions";
import { NOTIFICATION_TYPES } from "src/constants/notificationTypes";
import { NOTIFICATION_HEADERS } from "src/constants/notificationHeaders";

const SettingsGeneralFormView = ({
    isShopify,
    handleSubmit,
    values,
    handleChange,
    isSubmitting,
    setFieldValue,
    shouldHaveAllLocationsActive,
}) => {
    const { getAppBridge } = useShopifyAppBridge();
    const router = useRouter();

    const dispatch = useDispatch();

    const updateLocationVisibility = async (e, locationId, isVisible) => {
        try {
            await Api.request({
                method: "POST",
                url: `/retailer/settings/locations/${locationId}/visibility`,
                data: { isVisible: !isVisible },
            });

            return true;
        } catch (error) {
            return false;
        }
    };

    const updateLocationBillability = async (e, locationId, isBillable) => {
        const hasUserAgreedToChanges = confirm(
            `You are about to ${
                !isBillable ? "activate" : "deactivate"
            } a location. Only active locations are bookable. Are you sure?`
        );

        if (!hasUserAgreedToChanges) {
            return false;
        }

        try {
            if (isShopify) {
                const { data } = await Api.request({
                    method: "POST",
                    url: `/retailer/settings/shopify/locations/${locationId}/billability`,
                    data: { isBillable: !isBillable },
                });

                if (!!data.redirect_url) {
                    const app = getAppBridge();
                    Redirect.create(app).dispatch(
                        Redirect.Action.REMOTE,
                        data.redirect_url
                    );
                }
            } else {
                await Api.request({
                    method: "POST",
                    url: `/retailer/settings/locations/${locationId}/billability`,
                    data: { isBillable: !isBillable },
                });
            }

            return true;
        } catch (error) {
            const message =
                error.response?.data || error.request || error.message;

            dispatch(
                showNotification(
                    message,
                    NOTIFICATION_TYPES.WARNING,
                    NOTIFICATION_HEADERS.RETAILER_SUBSCRIPTION
                )
            );
            return false;
        }
    };

    return (
        <Fragment>
            <Form onSubmit={handleSubmit} className={styles.RetailerSettings}>
                <section>
                    <h2>Retailer Info</h2>

                    <FormGroup controlId="input-name">
                        <FormLabel>Name</FormLabel>
                        <FormControl
                            type="text"
                            value={values.name}
                            name="name"
                            onChange={handleChange}
                        />
                        <ErrorMessage
                            className="error-message"
                            component="div"
                            name={"name"}
                        />
                    </FormGroup>
                    <FormGroup controlId="input-url">
                        <FormLabel>Website URL</FormLabel>
                        <FormControl
                            type="text"
                            value={values.websiteUrl}
                            name="websiteUrl"
                            onChange={handleChange}
                        />
                        <ErrorMessage
                            className="error-message"
                            component="div"
                            name={"websiteUrl"}
                        />
                        <FormText muted>
                            This should be the URL where you've installed the
                            Tote widget.
                        </FormText>
                    </FormGroup>
                    <FormGroup controlId="input-email">
                        <FormLabel>Email addresses</FormLabel>
                        <p className={styles.AdditionalLabelInfo}>
                            First email address should be store's primary email.
                        </p>

                        <FieldArray
                            name="additionalEmails"
                            render={(arrayHelpers) => {
                                return (
                                    <>
                                        <FormListSingleFieldItem
                                            type={"email"}
                                            placeholder={"Email"}
                                            name={"email"}
                                            buttonText={"Add Email"}
                                            buttonOnClick={() =>
                                                arrayHelpers.push("")
                                            }
                                        />

                                        {values.additionalEmails.map((_, i) => {
                                            return (
                                                <FormListSingleFieldItem
                                                    key={i}
                                                    placeholder={"Email"}
                                                    type={"email"}
                                                    name={`additionalEmails[${i}]`}
                                                    buttonText={"x"}
                                                    buttonOnClick={() =>
                                                        arrayHelpers.remove(i)
                                                    }
                                                />
                                            );
                                        })}
                                    </>
                                );
                            }}
                        />
                        <FormText muted>
                            Confirmation emails and calendar invites will be
                            sent to these email addresses.
                        </FormText>
                    </FormGroup>
                    <FormGroup controlId="input-email">
                        <FormLabel>Addresses</FormLabel>
                        <p
                            className={`${styles.AdditionalLabelInfo} ${styles.ImportantInformation}`}
                        >
                            Enter full addresses. Example: 900 Broadway, New
                            York, NY 10003
                        </p>
                        <FieldArray
                            name="additionalAddresses"
                            render={(arrayHelpers) => {
                                return (
                                    <>
                                        <>
                                            <div
                                                className={
                                                    styles.RetailerListItemContainer
                                                }
                                            >
                                                <FormControl
                                                    className={
                                                        styles.NeighborhoodField
                                                    }
                                                    type="text"
                                                    value={
                                                        values.address
                                                            .neighborhood
                                                    }
                                                    name={
                                                        "address.neighborhood"
                                                    }
                                                    onChange={handleChange}
                                                    placeholder={"Neighborhood"}
                                                    disabled={
                                                        values.isRetailerOnlineOnly
                                                    }
                                                />
                                                <FormControl
                                                    className={
                                                        styles.AddressField
                                                    }
                                                    type="text"
                                                    value={
                                                        values.address.address
                                                    }
                                                    name={"address.address"}
                                                    onChange={handleChange}
                                                    placeholder={"Address"}
                                                    disabled={
                                                        values.isRetailerOnlineOnly
                                                    }
                                                />
                                                {values.address.locationId &&
                                                    !shouldHaveAllLocationsActive && (
                                                        <FormListButton
                                                            text={
                                                                values.address
                                                                    .isBillable
                                                                    ? "Activated"
                                                                    : "Deactivated"
                                                            }
                                                            onClick={(e) => {
                                                                updateLocationBillability(
                                                                    e,
                                                                    values
                                                                        .address
                                                                        .locationId,
                                                                    values
                                                                        .address
                                                                        .isBillable
                                                                ).then((x) => {
                                                                    if (x) {
                                                                        setFieldValue(
                                                                            `address.isBillable`,
                                                                            !values
                                                                                .address
                                                                                .isBillable
                                                                        );
                                                                    }
                                                                });
                                                            }}
                                                            disabled={
                                                                values.isRetailerOnlineOnly
                                                            }
                                                        />
                                                    )}
                                                {values.address.locationId && (
                                                    <FormListButton
                                                        text={
                                                            values.address
                                                                .isVisible
                                                                ? "Visible"
                                                                : "Hidden"
                                                        }
                                                        onClick={(e) => {
                                                            updateLocationVisibility(
                                                                e,
                                                                values.address
                                                                    .locationId,
                                                                values.address
                                                                    .isVisible
                                                            ).then((x) => {
                                                                if (x) {
                                                                    setFieldValue(
                                                                        `address.isVisible`,
                                                                        !values
                                                                            .address
                                                                            .isVisible
                                                                    );
                                                                }
                                                            });
                                                        }}
                                                        disabled={
                                                            values.isRetailerOnlineOnly
                                                        }
                                                    />
                                                )}
                                                <FormListButton
                                                    className={
                                                        styles.addAddressButton
                                                    }
                                                    text={"Add Address"}
                                                    onClick={() =>
                                                        arrayHelpers.push({
                                                            ...ADDRESS_INITIAL_VALUES,
                                                        })
                                                    }
                                                    disabled={
                                                        values.isRetailerOnlineOnly
                                                    }
                                                />
                                            </div>
                                            <ErrorMessage
                                                name="address"
                                                className="error-message"
                                                component="div"
                                            />
                                        </>

                                        {values.additionalAddresses.map(
                                            (_, i) => {
                                                return (
                                                    <div key={i}>
                                                        <div
                                                            className={
                                                                styles.RetailerListItemContainer
                                                            }
                                                        >
                                                            <Field
                                                                className={`form-control ${styles.NeighborhoodField}`}
                                                                type={"text"}
                                                                name={`additionalAddresses[${i}].neighborhood`}
                                                                placeholder={
                                                                    "Neighborhood"
                                                                }
                                                                disabled={
                                                                    values.isRetailerOnlineOnly
                                                                }
                                                            />
                                                            <Field
                                                                className={`form-control ${styles.AddressField}`}
                                                                type={"text"}
                                                                name={`additionalAddresses[${i}].address`}
                                                                placeholder={
                                                                    "Address"
                                                                }
                                                                disabled={
                                                                    values.isRetailerOnlineOnly
                                                                }
                                                            />
                                                            {values
                                                                .additionalAddresses[
                                                                i
                                                            ].locationId &&
                                                                !shouldHaveAllLocationsActive && (
                                                                    <FormListButton
                                                                        text={
                                                                            values
                                                                                .additionalAddresses[
                                                                                i
                                                                            ]
                                                                                .isBillable
                                                                                ? "Activated"
                                                                                : "Deactivated"
                                                                        }
                                                                        onClick={(
                                                                            e
                                                                        ) => {
                                                                            updateLocationBillability(
                                                                                e,
                                                                                values
                                                                                    .additionalAddresses[
                                                                                    i
                                                                                ]
                                                                                    .locationId,
                                                                                values
                                                                                    .additionalAddresses[
                                                                                    i
                                                                                ]
                                                                                    .isBillable
                                                                            ).then(
                                                                                (
                                                                                    x
                                                                                ) => {
                                                                                    if (
                                                                                        x
                                                                                    ) {
                                                                                        setFieldValue(
                                                                                            `additionalAddresses[${i}].isBillable`,
                                                                                            !values
                                                                                                .additionalAddresses[
                                                                                                i
                                                                                            ]
                                                                                                .isBillable
                                                                                        );
                                                                                    }
                                                                                }
                                                                            );
                                                                        }}
                                                                        disabled={
                                                                            values.isRetailerOnlineOnly
                                                                        }
                                                                    />
                                                                )}
                                                            {values
                                                                .additionalAddresses[
                                                                i
                                                            ].locationId && (
                                                                <FormListButton
                                                                    text={
                                                                        values
                                                                            .additionalAddresses[
                                                                            i
                                                                        ]
                                                                            .isVisible
                                                                            ? "Visible"
                                                                            : "Hidden"
                                                                    }
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        updateLocationVisibility(
                                                                            e,
                                                                            values
                                                                                .additionalAddresses[
                                                                                i
                                                                            ]
                                                                                .locationId,
                                                                            values
                                                                                .additionalAddresses[
                                                                                i
                                                                            ]
                                                                                .isVisible
                                                                        ).then(
                                                                            (
                                                                                x
                                                                            ) => {
                                                                                if (
                                                                                    x
                                                                                ) {
                                                                                    setFieldValue(
                                                                                        `additionalAddresses[${i}].isVisible`,
                                                                                        !values
                                                                                            .additionalAddresses[
                                                                                            i
                                                                                        ]
                                                                                            .isVisible
                                                                                    );
                                                                                }
                                                                            }
                                                                        );
                                                                    }}
                                                                    disabled={
                                                                        values.isRetailerOnlineOnly
                                                                    }
                                                                />
                                                            )}
                                                            <FormListButton
                                                                text={"x"}
                                                                onClick={() =>
                                                                    arrayHelpers.remove(
                                                                        i
                                                                    )
                                                                }
                                                                disabled={
                                                                    values.isRetailerOnlineOnly
                                                                }
                                                            />
                                                        </div>
                                                        <ErrorMessage
                                                            name={`additionalAddresses[${i}]`}
                                                            className="error-message"
                                                            component="div"
                                                        />
                                                    </div>
                                                );
                                            }
                                        )}
                                    </>
                                );
                            }}
                        />
                    </FormGroup>

                    <FormCheck
                        custom
                        type="checkbox"
                        id="is-store-online-only"
                        label="We don't have a physical street address"
                        checked={values.isRetailerOnlineOnly}
                        name={`isRetailerOnlineOnly`}
                        onChange={handleChange}
                    />
                </section>

                <Button type="submit" disabled={isSubmitting} className="mb-4">
                    Save
                </Button>
            </Form>
        </Fragment>
    );
};

export default SettingsGeneralFormView;
